import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const PRIMARY = '#FF6B6B';
const SECONDARY = '#4ECDC4';
const ACCENT = '#FFE66D';
const BG_TOP = '#08090c';
const BG_MID = '#0B1120';
const BG_BOT = '#0F1419';

const TOTAL_FRAMES = 1080;

const PHASE = {
  title: [0, 275] as const,
  pareto: [265, 610] as const,
  circle: [600, 820] as const,
  examples: [810, 970] as const,
  apply: [960, 1080] as const,
};

function phaseOpacity(
  frame: number,
  enterStart: number,
  enterEnd: number,
  exitStart: number,
  exitEnd: number,
): number {
  const fadeIn = interpolate(frame, [enterStart, enterEnd], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  if (exitStart >= exitEnd) {
    return fadeIn;
  }
  const fadeOut = interpolate(frame, [exitStart, exitEnd], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return fadeIn * fadeOut;
}

const PARETO_ITEMS = [
  { cause: '原因 A', value: 42, cum: 42 },
  { cause: '原因 B', value: 28, cum: 70 },
  { cause: '原因 C', value: 15, cum: 85 },
  { cause: '原因 D', value: 8, cum: 93 },
  { cause: '其他', value: 7, cum: 100 },
];

const ParetoChart: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const chartStart = PHASE.pareto[0] + 30;
  const W = 1200;
  const H = 640;
  const padL = 110;
  const padR = 50;
  const padT = 50;
  const padB = 70;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barGap = 28;
  const barW = (innerW - barGap * (PARETO_ITEMS.length - 1)) / PARETO_ITEMS.length;
  const maxVal = 45;

  return (
    <svg width={W} height={H + 40} viewBox={`0 0 ${W} ${H + 40}`}>
      <defs>
        <linearGradient id="barGrad80" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.9} />
          <stop offset="100%" stopColor="#ff8787" stopOpacity={1} />
        </linearGradient>
        <linearGradient id="lineGrad80" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={SECONDARY} />
          <stop offset="100%" stopColor={ACCENT} />
        </linearGradient>
        <filter id="barGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect x={padL - 10} y={padT - 10} width={innerW + 20} height={innerH + 20} rx={18} fill="rgba(255,255,255,0.025)" stroke="rgba(255,255,255,0.06)" />

      {[0, 25, 50, 75, 100].map((pct) => {
        const y = padT + innerH - (pct / 100) * innerH;
        return (
          <g key={pct}>
            <line x1={padL} y1={y} x2={padL + innerW} y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray="4 6" />
            <text x={padL - 18} y={y + 6} fill="rgba(255,255,255,0.35)" fontSize={22} textAnchor="end" fontWeight={500}>
              {pct}%
            </text>
          </g>
        );
      })}

      {PARETO_ITEMS.map((item, i) => {
        const barDelay = chartStart + i * 20;
        const barProg = spring({
          frame: frame - barDelay,
          fps,
          config: { damping: 14, stiffness: 90 },
        });
        const h = (item.value / maxVal) * innerH * barProg;
        const x = padL + i * (barW + barGap);
        const baseY = padT + innerH;

        const lineDelay = chartStart + 90 + i * 18;
        const lineProg = spring({
          frame: frame - lineDelay,
          fps,
          config: { damping: 16, stiffness: 80 },
        });
        const dotCx = x + barW / 2;
        const dotCy = baseY - (item.cum / 100) * innerH * lineProg;

        const isTop = i < 2;
        return (
          <g key={item.cause}>
            <rect
              x={x}
              y={baseY - h}
              width={barW}
              height={h}
              rx={12}
              fill="url(#barGrad80)"
              filter={isTop ? 'url(#barGlow)' : undefined}
              opacity={isTop ? 1 : 0.75}
            />
            {barProg > 0.5 && (
              <text
                x={x + barW / 2}
                y={baseY - h - 16}
                textAnchor="middle"
                fill={isTop ? ACCENT : 'rgba(255,255,255,0.5)'}
                fontSize={26}
                fontWeight={700}
                opacity={interpolate(barProg, [0.5, 1], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
              >
                {item.value}%
              </text>
            )}
            <text
              x={x + barW / 2}
              y={baseY + 36}
              textAnchor="middle"
              fill="rgba(255,255,255,0.55)"
              fontSize={24}
              fontWeight={600}
            >
              {item.cause}
            </text>
            {lineProg > 0.05 && (
              <circle cx={dotCx} cy={dotCy} r={8} fill={SECONDARY} opacity={lineProg} stroke={BG_MID} strokeWidth={2.5} />
            )}
          </g>
        );
      })}

      <path
        d={PARETO_ITEMS.map((item, i) => {
          const x = padL + i * (barW + barGap) + barW / 2;
          const y = padT + innerH - (item.cum / 100) * innerH;
          return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
        }).join(' ')}
        fill="none"
        stroke="url(#lineGrad80)"
        strokeWidth={4.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={interpolate(frame, [chartStart + 150, chartStart + 210], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })}
        strokeDasharray="1200"
        strokeDashoffset={interpolate(
          frame,
          [chartStart + 150, chartStart + 280],
          [1200, 0],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
        )}
      />

      {interpolate(frame, [chartStart + 160, chartStart + 220], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      }) > 0.3 && (
        <line
          x1={padL + 2 * (barW + barGap) - barGap / 2}
          y1={padT}
          x2={padL + 2 * (barW + barGap) - barGap / 2}
          y2={padT + innerH}
          stroke={ACCENT}
          strokeWidth={2.5}
          strokeDasharray="10 5"
          opacity={interpolate(frame, [chartStart + 160, chartStart + 220], [0, 0.6], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        />
      )}

      <text x={W / 2} y={H + 34} textAnchor="middle" fill={ACCENT} fontSize={26} fontWeight={700}>
        柱状：各原因占比 · 折线：累计贡献 · 虚线：80% 分界
      </text>
    </svg>
  );
};

const SplitCircle: React.FC<{ progress: number }> = ({ progress }) => {
  const r = 240;
  const cx = 280;
  const cy = 280;
  const angle20 = (20 / 100) * 2 * Math.PI * progress;
  const angle80 = (80 / 100) * 2 * Math.PI * progress;
  const x1 = cx + r * Math.sin(0);
  const y1 = cy - r * Math.cos(0);
  const x2 = cx + r * Math.sin(angle20);
  const y2 = cy - r * Math.cos(angle20);
  const large20 = angle20 > Math.PI ? 1 : 0;
  const path20 = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large20} 1 ${x2} ${y2} Z`;
  const x3 = cx + r * Math.sin(angle20);
  const y3 = cy - r * Math.cos(angle20);
  const x4 = cx + r * Math.sin(angle20 + angle80);
  const y4 = cy - r * Math.cos(angle20 + angle80);
  const large80 = angle80 > Math.PI ? 1 : 0;
  const path80 = `M ${cx} ${cy} L ${x3} ${y3} A ${r} ${r} 0 ${large80} 1 ${x4} ${y4} Z`;

  return (
    <svg width={580} height={560} viewBox="0 0 580 560">
      <defs>
        <filter id="glow8020" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="12" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id="circleBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.08} />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <rect width={580} height={560} fill="url(#circleBg)" />
      <g filter="url(#glow8020)">
        <path d={path20} fill={ACCENT} opacity={0.92} />
        <path d={path80} fill={PRIMARY} opacity={0.85} />
      </g>
      <circle cx={cx} cy={cy} r={72} fill={BG_MID} stroke={SECONDARY} strokeWidth={3.5} />
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#fff" fontSize={32} fontWeight={800}>
        聚焦
      </text>
      <text x={cx} y={cy + 24} textAnchor="middle" fill={SECONDARY} fontSize={24} fontWeight={600}>
        关键少数
      </text>
      {progress > 0.5 && (
        <>
          <text x={cx + r * 0.35} y={cy - r * 0.48} fill={ACCENT} fontSize={40} fontWeight={900}>
            20%
          </text>
          <text x={cx + r * 0.35} y={cy - r * 0.22} fill="rgba(0,0,0,0.55)" fontSize={26} fontWeight={600}>
            努力
          </text>
          <text x={cx - r * 0.2} y={cy + r * 0.52} fill="#fff" fontSize={40} fontWeight={900}>
            80%
          </text>
          <text x={cx - r * 0.2} y={cy + r * 0.76} fill="rgba(255,255,255,0.5)" fontSize={26} fontWeight={600}>
            结果
          </text>
        </>
      )}
    </svg>
  );
};

const CauseArrow: React.FC<{ t: number }> = ({ t }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 28, marginTop: 30 }}>
    <div
      style={{
        padding: '22px 36px',
        borderRadius: 18,
        background: `linear-gradient(135deg, ${PRIMARY}22, ${PRIMARY}0a)`,
        border: `2px solid ${PRIMARY}55`,
        fontSize: 26,
        fontWeight: 700,
        color: PRIMARY,
        opacity: t,
        transform: `scale(${interpolate(t, [0, 1], [0.85, 1])})`,
      }}
    >
      20% 关键原因
    </div>
    <div
      style={{
        fontSize: 38,
        color: SECONDARY,
        fontWeight: 300,
        opacity: interpolate(t, [0.4, 1], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }),
      }}
    >
      →
    </div>
    <div
      style={{
        padding: '22px 36px',
        borderRadius: 18,
        background: `linear-gradient(135deg, ${SECONDARY}22, ${SECONDARY}0a)`,
        border: `2px solid ${SECONDARY}55`,
        fontSize: 26,
        fontWeight: 700,
        color: SECONDARY,
        opacity: t,
        transform: `scale(${interpolate(t, [0, 1], [0.85, 1])})`,
      }}
    >
      80% 结果产出
    </div>
  </div>
);

const EXAMPLE_CARDS = [
  {
    title: '财富分布',
    stat: '~80% 财富',
    sub: '集中于约 20% 人群（示意）',
    detail: '理解结构性差异，关注可复制的少数策略',
    border: PRIMARY,
  },
  {
    title: '软件缺陷',
    stat: '~80% 故障',
    sub: '往往由 20% 模块引入',
    detail: '测试与重构优先覆盖高风险代码路径',
    border: SECONDARY,
  },
  {
    title: '销售业绩',
    stat: '~80% 收入',
    sub: '常来自 20% 客户或 SKU',
    detail: '资源倾斜与服务深度绑定高价值关系',
    border: ACCENT,
  },
  {
    title: '学习时间',
    stat: '~80% 掌握度',
    sub: '来自 20% 核心概念',
    detail: '先攻克主干知识图谱，再扩展细节',
    border: '#f472b6',
  },
];

const APPLY_STEPS = [
  '列出所有任务 / 原因，用数据或经验排序贡献度',
  '圈出累计贡献接近 80% 的头部项（通常约 20% 数量）',
  '为头部项分配主要时间与预算，刻意减少长尾消耗',
  '周期性复盘：关键少数是否随环境变化而迁移',
];

export const EightyTwentyRuleAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleS = spring({ frame, fps, config: { damping: 16, stiffness: 100 } });
  const introCardS = spring({ frame: frame - 18, fps, config: { damping: 16, stiffness: 90 } });
  const principleCardS = spring({ frame: frame - 42, fps, config: { damping: 16, stiffness: 85 } });

  const opTitle = phaseOpacity(frame, PHASE.title[0], PHASE.title[0] + 22, PHASE.title[1] - 25, PHASE.title[1]);
  const opPareto = phaseOpacity(frame, PHASE.pareto[0] + 8, PHASE.pareto[0] + 28, PHASE.pareto[1] - 22, PHASE.pareto[1]);
  const opCircle = phaseOpacity(frame, PHASE.circle[0] + 8, PHASE.circle[0] + 30, PHASE.circle[1] - 22, PHASE.circle[1]);
  const opExamples = phaseOpacity(
    frame,
    PHASE.examples[0] + 8,
    PHASE.examples[0] + 28,
    PHASE.examples[1] - 22,
    PHASE.examples[1],
  );
  const opApply = phaseOpacity(frame, PHASE.apply[0] + 8, PHASE.apply[0] + 30, TOTAL_FRAMES, TOTAL_FRAMES);

  const circleProg = interpolate(frame, [PHASE.circle[0] + 25, PHASE.circle[0] + 130], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const arrowT = spring({
    frame: frame - 65,
    fps,
    config: { damping: 14, stiffness: 85 },
  });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(172deg, ${BG_TOP} 0%, ${BG_MID} 48%, ${BG_BOT} 100%)`,
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: '#fff',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 75% 20%, ${PRIMARY}14 0%, transparent 42%),
            radial-gradient(ellipse at 15% 80%, ${SECONDARY}12 0%, transparent 38%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          left: 72,
          top: 44,
          opacity: opTitle * titleS,
          transform: `translateY(${interpolate(titleS, [0, 1], [48, 0])}px)`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 58,
            fontWeight: 900,
            letterSpacing: -0.5,
            background: `linear-gradient(115deg, ${PRIMARY}, ${ACCENT}, ${SECONDARY})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          帕累托法则
        </h1>
        <p style={{ margin: '8px 0 0', fontSize: 30, fontWeight: 700, color: SECONDARY }}>80 / 20 效率法则</p>
        <p style={{ margin: '4px 0 0', fontSize: 26, color: 'rgba(255,255,255,0.4)' }}>
          Pareto Principle · 少数驱动多数
        </p>
      </div>

      {/* Intro Card — 核心命题 */}
      <div
        style={{
          position: 'absolute',
          left: 80,
          right: 80,
          top: 310,
          opacity: opTitle * introCardS,
          transform: `translateY(${interpolate(introCardS, [0, 1], [30, 0])}px)`,
        }}
      >
        <div
          style={{
            padding: '28px 38px',
            borderRadius: 24,
            background: 'linear-gradient(125deg, rgba(255,107,107,0.14) 0%, rgba(78,205,196,0.1) 100%)',
            border: `1px solid rgba(255,230,109,0.25)`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 36, fontWeight: 800, color: ACCENT }}>核心命题</h2>
          <p style={{ margin: '14px 0 0', fontSize: 30, lineHeight: 1.55, color: 'rgba(255,255,255,0.88)' }}>
            在许多系统中，大约 <strong style={{ color: PRIMARY }}>80% 的结果</strong> 来自{' '}
            <strong style={{ color: SECONDARY }}>20% 的原因</strong>。
            它不是精确公式，而是提醒我们把火力集中在高杠杆区域。
          </p>
          <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
            {['高杠杆', '关键少数', '资源聚焦', '持续迭代'].map((tag) => (
              <span
                key={tag}
                style={{
                  padding: '6px 18px',
                  borderRadius: 999,
                  background: `${SECONDARY}22`,
                  color: SECONDARY,
                  fontSize: 24,
                  fontWeight: 600,
                  border: `1px solid ${SECONDARY}44`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Principle — 不对称分布 */}
      <div
        style={{
          position: 'absolute',
          left: 80,
          right: 80,
          top: 590,
          opacity: opTitle * principleCardS,
          transform: `translateY(${interpolate(principleCardS, [0, 1], [30, 0])}px)`,
        }}
      >
        <div
          style={{
            padding: '28px 38px',
            borderRadius: 24,
            background: 'linear-gradient(135deg, rgba(255,107,107,0.1) 0%, rgba(78,205,196,0.08) 100%)',
            border: `1px solid rgba(255,230,109,0.2)`,
          }}
        >
          <h2 style={{ margin: '0 0 14px', fontSize: 36, fontWeight: 800, color: ACCENT }}>
            不对称分布：少数驱动多数
          </h2>
          <p style={{ margin: 0, fontSize: 30, lineHeight: 1.55, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
            约 <span style={{ color: PRIMARY }}>80% 的结果</span> 由{' '}
            <span style={{ color: SECONDARY }}>20% 的原因</span> 驱动——
            识别并加码高杠杆区域，远比"平均用力"更有效率。
          </p>
          <CauseArrow t={arrowT} />
          <p style={{ margin: '18px 0 0', fontSize: 24, color: 'rgba(255,255,255,0.38)' }}>
            Vilfredo Pareto 于 1896 年观察到意大利 80% 土地归 20% 人口所有
          </p>
        </div>
      </div>

      {/* Pareto Chart */}
      <div
        style={{
          position: 'absolute',
          left: 40,
          right: 40,
          top: 120,
          opacity: opPareto,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 46, fontWeight: 800, color: PRIMARY, marginBottom: 10 }}>
          帕累托图：双轴读懂"关键少数"
        </h2>
        <p style={{ textAlign: 'center', fontSize: 26, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>
          条形显示单项占比，折线显示从左到右的累计百分比
        </p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ParetoChart frame={frame} fps={fps} />
        </div>
      </div>

      {/* Circle */}
      <div
        style={{
          position: 'absolute',
          left: 40,
          top: 260,
          opacity: opCircle,
          display: 'flex',
          width: 'calc(100% - 80px)',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 56,
        }}
      >
        <SplitCircle progress={circleProg} />
        <div style={{ maxWidth: 680 }}>
          <h2 style={{ fontSize: 52, fontWeight: 800, color: SECONDARY, margin: '0 0 22px' }}>20% 与 80% 的直觉模型</h2>
          <p style={{ fontSize: 32, lineHeight: 1.65, color: 'rgba(255,255,255,0.78)', margin: 0 }}>
            把圆盘想象成时间与精力的分配：黄色扇区是你最值得加码的"少数关键动作"；红色大块代表它们能撬动的绝大部分成果。
            管理的目标不是平均用力，而是持续识别并刷新这 20%。
          </p>
          <ul style={{ margin: '26px 0 0', paddingLeft: 26, fontSize: 30, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
            <li>优先清单应短而硬，拒绝伪忙碌</li>
            <li>长尾问题可以标准化、自动化或委派</li>
            <li>警惕"平均优化"稀释了头部收益</li>
          </ul>
        </div>
      </div>

      {/* Examples */}
      <div
        style={{
          position: 'absolute',
          left: 64,
          right: 64,
          top: 190,
          opacity: opExamples,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 46, fontWeight: 800, color: ACCENT, marginBottom: 60 }}>
          真实世界中的 80/20 视角
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 24,
          }}
        >
          {EXAMPLE_CARDS.map((c, i) => {
            const p = spring({
              frame: frame - PHASE.examples[0] - 14 - i * 14,
              fps,
              config: { damping: 14, stiffness: 95 },
            });
            return (
              <div
                key={c.title}
                style={{
                  padding: '30px 36px',
                  borderRadius: 22,
                  background: `linear-gradient(135deg, ${c.border}12, rgba(255,255,255,0.02))`,
                  border: `2px solid ${c.border}33`,
                  boxShadow: `0 0 36px ${c.border}12`,
                  opacity: p,
                  transform: `translateY(${interpolate(p, [0, 1], [36, 0])}px)`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 34, fontWeight: 800, color: c.border }}>{c.title}</span>
                  <span style={{ fontSize: 28, fontWeight: 800, color: ACCENT }}>{c.stat}</span>
                </div>
                <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.45)', marginTop: 8 }}>{c.sub}</div>
                <p style={{ margin: '16px 0 0', fontSize: 26, lineHeight: 1.55, color: 'rgba(255,255,255,0.82)' }}>
                  {c.detail}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Apply */}
      <div
        style={{
          position: 'absolute',
          left: 100,
          right: 100,
          top: 220,
          opacity: opApply,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 40, fontWeight: 800, color: ACCENT, marginBottom: 42 }}>
          如何落地运用
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1100, margin: '0 auto' }}>
          {APPLY_STEPS.map((step, i) => {
            const s = spring({
              frame: frame - PHASE.apply[0] - 10 - i * 14,
              fps,
              config: { damping: 15, stiffness: 100 },
            });
            return (
              <div
                key={step}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 22,
                  padding: '24px 30px',
                  borderRadius: 20,
                  background: `linear-gradient(90deg, ${SECONDARY}14 0%, transparent 60%)`,
                  borderLeft: `5px solid ${PRIMARY}`,
                  border: `1px solid rgba(255,255,255,0.06)`,
                  borderLeftWidth: 5,
                  borderLeftColor: PRIMARY,
                  opacity: s,
                  transform: `translateX(${interpolate(s, [0, 1], [-40, 0])}px)`,
                }}
              >
                <span
                  style={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 14,
                    background: `linear-gradient(135deg, ${ACCENT}, ${PRIMARY})`,
                    color: BG_BOT,
                    fontWeight: 900,
                    fontSize: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ fontSize: 28, lineHeight: 1.5, color: 'rgba(255,255,255,0.88)', fontWeight: 500 }}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>
        <p style={{ textAlign: 'center', marginTop: 28, fontSize: 26, color: 'rgba(255,255,255,0.38)' }}>
          法则的价值在于持续提问：此刻最重要的 20% 是什么？
        </p>
      </div>
    </AbsoluteFill>
  );
};
